import { useCallback, useState } from "react";
import { useLazyExportProjectQuery } from "../../../app/features/project/projectApiSlice";
import { useLazyGetFastActivitiesByProjectQuery } from "../../../app/features/activities/activityApiSlice";
import { Tool } from "../../../app/features/auth/authTypes";
import { FastProject } from "../../../types/interfaces";
import type { FastActivityResponse } from "../../../app/features/activities/activityTypes";

interface UseExportProjectReturn {
    handleExportProject: (project: FastProject) => Promise<void>;
    isExportWarningOpen: boolean;
    handleConfirmExport: () => void;
    handleCancelExport: () => void;
}

const useExportProject = (): UseExportProjectReturn => {
    const [triggerExport] = useLazyExportProjectQuery();
    const [getFastActivities] = useLazyGetFastActivitiesByProjectQuery();
    const [isExportWarningOpen, setIsExportWarningOpen] = useState(false);
    const [pendingProject, setPendingProject] = useState<FastProject | null>(null);

    const executeExport = useCallback((project: FastProject) => {
        triggerExport({ id: project.id, projectName: project.name });
    }, [triggerExport]);

    const handleExportProject = useCallback(async (project: FastProject) => {
        if (!project?.id) {
            console.warn("No project ID found for export");
            return;
        }

        try {
            const result = await getFastActivities({ projectId: project.id, pageSize: 1, tool: Tool.B_INTACT }).unwrap() as FastActivityResponse;
            if (result.count > 0) {
                setPendingProject(project);
                setIsExportWarningOpen(true);
            } else {
                executeExport(project);
            }
        } catch {
            executeExport(project);
        }
    }, [getFastActivities, executeExport]);

    const handleConfirmExport = useCallback(() => {
        setIsExportWarningOpen(false);
        if (pendingProject) {
            executeExport(pendingProject);
            setPendingProject(null);
        }
    }, [pendingProject, executeExport]);

    const handleCancelExport = useCallback(() => {
        setIsExportWarningOpen(false);
        setPendingProject(null);
    }, []);

    return {
        handleExportProject,
        isExportWarningOpen,
        handleConfirmExport,
        handleCancelExport,
    };
};

export default useExportProject;
